import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ButtonComponent, IconComponent, TranslatePipe, translator } from '@softline/ui-core';
import { DsTabsComponent, TabItem } from '@softline/ui-core/tabs';
import { SelectDsComponent, SelectDsItem } from '@softline/ui-core/select';
import { ConditionFilter, FieldFilter, Filter } from '../../../../../types/filter';
import { FilterOperator } from '../../../../../types/filter-operators';
import { getBuilderOperators } from '../../../../../helper/builder-operators.function';
import { FilterTreeEditor } from '../filter-tree-editor';
import { FilterValueInputComponent } from '../filter-value-input/filter-value-input.component';

@Component({
  selector: 'soft-filter-node',
  imports: [
    ButtonComponent,
    IconComponent,
    DsTabsComponent,
    SelectDsComponent,
    FilterValueInputComponent,
    TranslatePipe,
    FilterNodeComponent
  ],
  templateUrl: './filter-node.component.html',
  styleUrl: './filter-node.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterNodeComponent {
  private readonly t = translator();
  readonly editor = inject(FilterTreeEditor);

  readonly node = input.required<ConditionFilter<any>>();
  readonly depth = input(0);

  readonly operatorTabs = computed<TabItem<'AND' | 'OR'>[]>(() => [
    { title: `${this.t('#CORE_FILTER.CUSTOM.AND')()}`, value: 'AND' },
    { title: `${this.t('#CORE_FILTER.CUSTOM.OR')()}`, value: 'OR' },
  ]);

  readonly fieldItems = computed<SelectDsItem<string>[]>(() =>
    this.editor
      .config()
      .items.filter((o) => getBuilderOperators(o).length > 0)
      .map((o) => ({ value: o.field as string, label: `${o.title()}` }))
  );

  operatorItems(field: string): SelectDsItem<FilterOperator>[] {
    const config = this.editor.getItemConfig(field);
    if (!config) return [];
    return getBuilderOperators(config).map((o) => ({
      value: o,
      label: `${this.t(`#CORE_FILTER.OPERATORS.${o}`)()}`,
    }));
  }

  asCondition(filter: Filter<any>): FieldFilter<any> {
    return filter as FieldFilter<any>;
  }

  asGroup(filter: Filter<any>): ConditionFilter<any> {
    return filter as ConditionFilter<any>;
  }

  conditionValue(filter: Filter<any>): unknown {
    return (filter as { value?: unknown }).value ?? null;
  }
}
